import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { fetchTracks, createTrack, deleteTrack, fetchCategories, fetchReciters } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminContent() {
  const [tracks, setTracks] = useState([]);
  const [cats, setCats] = useState([]);
  const [reciters, setReciters] = useState([]);
  const [form, setForm] = useState({
    title: "", type: "audio", media_url: "", thumbnail: "", duration: "",
    reciter_id: "", category_id: "", description: "", year: "", featured: false,
  });

  const reload = () => fetchTracks({ limit: 200 }).then(setTracks).catch(() => {});

  useEffect(() => {
    reload();
    fetchCategories().then(setCats);
    fetchReciters().then(setReciters);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.media_url) return toast.error("Title and media URL are required");
    try {
      const payload = { ...form, year: form.year ? parseInt(form.year) : null };
      if (!payload.reciter_id) delete payload.reciter_id;
      if (!payload.category_id) delete payload.category_id;
      await createTrack(payload);
      toast.success("Track added");
      setForm({ title: "", type: "audio", media_url: "", thumbnail: "", duration: "", reciter_id: "", category_id: "", description: "", year: "", featured: false });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add track");
    }
  };

  const onDelete = async (id) => {
    await deleteTrack(id);
    reload();
  };

  return (
    <div data-testid="admin-content-page" className="space-y-10">
      <div>
        <div className="overline text-[#B79C7B]">Content</div>
        <h1 className="font-serif text-4xl text-ivory mt-2">Audio & Video Library</h1>
      </div>

      <section className="border border-[#2A2A2D] bg-[#121214] p-8">
        <div className="overline text-[#B79C7B] mb-6">Add new track</div>
        <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-4">
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="content-title" />
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="bg-[#0A0A0B] border-[#2A2A2D] h-11" data-testid="content-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#2A2A2D] text-ivory">
              <SelectItem value="audio">Audio</SelectItem>
              <SelectItem value="video">Video</SelectItem>
            </SelectContent>
          </Select>
          <input className="md:col-span-2 h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Media URL (mp3 or mp4)" value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })} data-testid="content-url" />
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Thumbnail URL" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} data-testid="content-thumb" />
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Duration (e.g. 04:32)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="content-duration" />
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger className="bg-[#0A0A0B] border-[#2A2A2D] h-11" data-testid="content-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#2A2A2D] text-ivory">
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.reciter_id} onValueChange={(v) => setForm({ ...form, reciter_id: v })}>
            <SelectTrigger className="bg-[#0A0A0B] border-[#2A2A2D] h-11" data-testid="content-reciter"><SelectValue placeholder="Reciter" /></SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#2A2A2D] text-ivory">
              {reciters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <input className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory" placeholder="Year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="content-year" />
          <label className="flex items-center gap-2 text-sm text-[#A1A1AA] h-11 px-4 border border-[#2A2A2D] bg-[#0A0A0B]">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="content-featured" />
            Mark as featured
          </label>
          <textarea className="md:col-span-2 min-h-[80px] bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 py-3 text-ivory" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button type="submit" className="md:col-span-2 h-11 bg-[#8A2E2E] hover:bg-[#A33B3B] text-sm tracking-widest flex items-center justify-center gap-2" data-testid="add-track-button">
            <Plus size={16} /> ADD TRACK
          </button>
        </form>
      </section>

      <section>
        <div className="overline text-[#B79C7B] mb-4">All tracks · {tracks.length}</div>
        <div className="border border-[#2A2A2D]">
          <div className="grid grid-cols-[1fr_120px_180px_120px_60px] px-5 py-3 bg-[#121214] border-b border-[#2A2A2D] overline text-[#71717A]">
            <div>Title</div><div>Type</div><div>Reciter</div><div>Category</div><div></div>
          </div>
          {tracks.map((t) => (
            <div key={t.id} className="grid grid-cols-[1fr_120px_180px_120px_60px] items-center px-5 py-3 border-b border-[#2A2A2D] text-sm" data-testid={`track-row-admin-${t.id}`}>
              <div className="text-ivory truncate">{t.title}</div>
              <div className="text-[#A1A1AA] uppercase text-xs">{t.type}</div>
              <div className="text-[#A1A1AA] truncate">{t.reciter_name}</div>
              <div className="text-[#A1A1AA] truncate">{t.category_slug}</div>
              <button onClick={() => onDelete(t.id)} className="text-[#71717A] hover:text-[#FF3B30]" data-testid={`delete-track-${t.id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
